import { useRef, useState, type FormEvent } from "react";
import { gql, useApolloClient } from "@apollo/client";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  FileText,
  TriangleAlert,
  Upload,
  UserCheck,
  X,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import type { Contact } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/checkbox";
import { getStdParams } from "@/components/DocProducer/buildDocumentProductionList";
import {
  SigneeSelector,
  type Signee,
} from "@/components/DocProducer/SigneeSelector";
import { Input } from "@/components/input";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";

const CUSTOMER_CLASSIFICATION_PROCESS_KEY = "capguard-classification";
const CUSTOMER_CLASSIFICATION_START_PROCESS = gql`
  mutation StartCustomerClassification($key: String, $data: Json) {
    startProcess(key: $key, data: $data) {
      formDefinition
      taskId
      processInstanceId
      data
    }
  }
`;

const CLASSIFICATION_OPTIONS = [
  {
    value: "natural",
    label: "Naturlig profesjonell",
    description: "Stort foretak · § 10-6",
    icon: Building2,
  },
  {
    value: "requested",
    label: "Profesjonell etter anmodning",
    description: "Opt-in · §§ 10-7 og 10-8",
    icon: UserCheck,
  },
] as const;

type ClassificationType = (typeof CLASSIFICATION_OPTIONS)[number]["value"];

const fieldCls =
  "mt-1.5 w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground/70 hover:border-border focus:border-primary focus:ring-2 focus:ring-primary/15";
const labelCls = "block text-xs font-medium text-foreground/90";
const groupCls = "rounded-lg border border-border/60 bg-muted/20 p-3.5";
const checkboxCls =
  "size-5 rounded-md border-border bg-background shadow-sm transition-colors hover:border-primary/60";
const checkboxLabelCls =
  "flex cursor-pointer select-none items-center gap-2.5 rounded-lg border border-border/60 bg-background px-3 py-2 transition-colors hover:border-primary/30 hover:bg-primary/5";

export type NaturalProfessionalVariables = {
  accountingPeriod: string;
  currency: string;
  balanceSheet: number | null;
  annualNetTurnover: number | null;
  equity: number | null;
  sourceBrreg: boolean;
  sourceAttachment: boolean;
  basisBalanceSheet: boolean;
  basisEquity: boolean;
  basisAnnualNetTurnover: boolean;
  rationaleCurrencyConversion: string;
  attachments: Array<{
    fileName: string;
    mimeType: string;
    content: string;
  }>;
};

type RequestedProfessionalVariables = {
  requestedScope: string;
  requestedLimitation: string;
  transactionActivity: boolean | null;
  financialPortfolioOver500000: boolean | null;
  relevantProfessionalExperience: boolean | null;
  additionalInformation: string;
  attachments: NaturalProfessionalVariables["attachments"];
  signees: Signee[];
};

type ClassificationSubmission = {
  classificationType: "naturalProfessional" | "professionalUponRequest";
} & Partial<NaturalProfessionalVariables> &
  Partial<RequestedProfessionalVariables>;

type ProcessState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success" }
  | { status: "error"; message: string };

function ComplianceSubmissionStatus({
  isSending,
  isSigning,
  contactName,
  reference,
  statusRefreshError,
}: {
  isSending: boolean;
  isSigning: boolean;
  contactName: string;
  reference: string | null;
  statusRefreshError: string | null;
}) {
  const destination = isSigning ? "signering" : "compliance";
  return (
    <div className="flex min-h-[340px] flex-col items-center justify-center gap-5 rounded-xl bg-gradient-to-b from-primary/5 to-transparent px-4 py-10 text-center sm:px-8">
      <div
        className={cn(
          "flex size-20 items-center justify-center rounded-full border shadow-sm",
          isSending
            ? "border-primary/20 bg-primary/10 text-primary"
            : "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
        )}
      >
        {isSending ? (
          <Upload className="size-8 animate-pulse motion-reduce:animate-none" aria-hidden="true" />
        ) : (
          <CheckCircle2 className="size-10" aria-hidden="true" />
        )}
      </div>
      <h3 role="status" className="text-xl font-semibold tracking-tight">
        {isSending ? `Sender til ${destination}…` : `Sendt til ${destination}`}
      </h3>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        {isSending
          ? `Sender opplysninger for ${contactName}. Vent til innsendingen er bekreftet.`
          : `Opplysningene for ${contactName} er sendt til ${destination}. Du kan nå lukke denne visningen.`}
      </p>
      {isSending ? (
        <div
          role="progressbar"
          aria-label={`Sender til ${destination}`}
          className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-primary/10"
        >
          <div className="h-full w-full animate-pulse rounded-full bg-primary/70 motion-reduce:animate-none" />
        </div>
      ) : (
        reference && (
          <div className="w-full max-w-md rounded-lg border border-border/60 bg-background/80 px-4 py-3">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Referanse
            </p>
            <p className="mt-1 break-all font-mono text-xs">{reference}</p>
          </div>
        )
      )}
      {statusRefreshError && (
        <p role="alert" className="max-w-md text-xs text-destructive">
          {statusRefreshError}
        </p>
      )}
    </div>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error(`Kunne ikke lese vedlegget ${file.name}.`));
    reader.onerror = () =>
      reject(
        reader.error ?? new Error(`Kunne ikke lese vedlegget ${file.name}.`)
      );
    reader.readAsDataURL(file);
  });
}

function parseAmount(value: FormDataEntryValue | null): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const amount = Number(value.trim().replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(amount) ? amount : null;
}

function ClassificationAttachments({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (selectedFiles: File[]) => {
    if (disabled) return;
    if (
      selectedFiles.some(
        (file) =>
          !file.name.toLowerCase().endsWith(".pdf") ||
          (file.type !== "" && file.type !== "application/pdf")
      )
    ) {
      setError("Kun PDF-filer er tillatt.");
      return;
    }
    setError(null);
    onChange([
      ...files,
      ...selectedFiles.filter(
        (file) =>
          !files.some(
            (existing) =>
              existing.name === file.name &&
              existing.size === file.size &&
              existing.lastModified === file.lastModified
          )
      ),
    ]);
  };

  return (
    <div className="space-y-2">
      <p className={labelCls}>Vedlegg</p>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setIsDragging(false);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          addFiles(Array.from(event.dataTransfer.files));
        }}
        className={cn(
          "flex flex-col items-center gap-2 rounded-lg border border-dashed p-5 text-xs transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border bg-muted/20",
          disabled && "opacity-50"
        )}
      >
        <Upload className="size-5 text-muted-foreground" aria-hidden="true" />
        <p className="text-muted-foreground">Dra og slipp PDF-filer her</p>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          hidden
          disabled={disabled}
          onChange={(event) => {
            addFiles(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Velg PDF
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs"
            >
              <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <Button
                type="button"
                variant="icon"
                size="xs"
                aria-label={`Fjern ${file.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                <X className="size-3.5" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClassificationAmountInput({
  name,
}: {
  name: string;
}) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const amount = parseAmount(value);
  const decimalPlaces = Math.min(value.split(/[.,]/)[1]?.length ?? 0, 20);
  const display =
    focused || amount === null ? value : formatNumber(amount, decimalPlaces);

  return (
    <span className="relative mt-1.5 block">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
        NOK
      </span>
      <input type="hidden" name={name} value={value} />
      <Input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={(event) => {
          const next = event.target.value.replace(/\s/g, "");
          if (/^\d*([.,]\d*)?$/.test(next)) setValue(next);
        }}
        onFocus={(event) => {
          setFocused(true);
          const input = event.currentTarget;
          requestAnimationFrame(() => input.select());
        }}
        onBlur={() => setFocused(false)}
        className="pl-14 text-xs tabular-nums md:text-xs"
      />
    </span>
  );
}

function NaturalProfessionalDraft({
  onSubmit,
  isSubmitting,
  isDisabled,
}: {
  onSubmit: (variables: NaturalProfessionalVariables) => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
}) {
  const [fileError, setFileError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFileError(null);
    const formData = new FormData(event.currentTarget);
    let attachments: NaturalProfessionalVariables["attachments"];
    try {
      attachments = await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          mimeType: "application/pdf",
          content: await readFileAsDataUrl(file),
        }))
      );
    } catch (error) {
      console.error("Could not prepare customer classification data:", error);
      setFileError(
        error instanceof Error ? error.message : "Kunne ikke lese vedleggene."
      );
      return;
    }
    const value = (name: string) => formData.get(name);
    const isChecked = (name: string) => value(name) === "true";
    await onSubmit({
      accountingPeriod: String(value("accountingPeriod") ?? "").trim(),
      currency: "NOK",
      balanceSheet: parseAmount(value("balanceSheet")),
      annualNetTurnover: parseAmount(value("annualNetTurnover")),
      equity: parseAmount(value("equity")),
      sourceBrreg: isChecked("sourceBrreg"),
      sourceAttachment: isChecked("sourceAttachment"),
      basisBalanceSheet: isChecked("basisBalanceSheet"),
      basisEquity: isChecked("basisEquity"),
      basisAnnualNetTurnover: isChecked("basisAnnualNetTurnover"),
      rationaleCurrencyConversion: String(
        value("rationaleCurrencyConversion") ?? ""
      ).trim(),
      attachments,
    });
  };

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <div className={groupCls}>
        <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Regnskapsgrunnlag
        </h4>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className={labelCls}>
            Regnskapsperiode
            <input
              name="accountingPeriod"
              type="text"
              className={fieldCls}
            />
          </label>
          <label className={labelCls}>
            Balansesum
            <ClassificationAmountInput name="balanceSheet" />
          </label>
          <label className={labelCls}>
            Årlig netto omsetning
            <ClassificationAmountInput name="annualNetTurnover" />
          </label>
          <label className={labelCls}>
            Egenkapital
            <ClassificationAmountInput name="equity" />
          </label>
        </div>
      </div>
      <fieldset className={groupCls}>
        <legend className="mb-2 text-xs font-medium">Kilde</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
          {["Brønnøysundregistrene", "Vedlegg"].map((source) => (
            <label
              key={source}
              htmlFor={`classification-source-${source}`}
              className={checkboxLabelCls}
            >
              <Checkbox
                id={`classification-source-${source}`}
                name={
                  source === "Brønnøysundregistrene"
                    ? "sourceBrreg"
                    : "sourceAttachment"
                }
                value="true"
                className={checkboxCls}
              />
              {source}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className={groupCls}>
        <legend className="mb-2 text-xs font-medium">
          Grunnlag for vurderingen (minimum to kriterier)
        </legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
          {[
            { label: "Balansesum", name: "basisBalanceSheet" },
            { label: "Egenkapital", name: "basisEquity" },
            { label: "Årlig netto omsetning", name: "basisAnnualNetTurnover" },
          ].map((criterion) => (
            <label
              key={criterion.name}
              htmlFor={`classification-criterion-${criterion.name}`}
              className={checkboxLabelCls}
            >
              <Checkbox
                id={`classification-criterion-${criterion.name}`}
                name={criterion.name}
                value="true"
                className={checkboxCls}
              />
              {criterion.label}
            </label>
          ))}
        </div>
      </fieldset>
      <label className={labelCls}>
        Begrunnelse
        <textarea
          name="rationaleCurrencyConversion"
          rows={3}
          className={fieldCls}
        />
      </label>
      <ClassificationAttachments
        files={files}
        onChange={setFiles}
        disabled={isSubmitting || isDisabled}
      />
      {fileError && (
        <p role="alert" className="text-xs text-destructive">
          {fileError}
        </p>
      )}
      <div className="flex flex-wrap gap-2 border-t border-border pt-3">
        <button
          type="submit"
          disabled={isSubmitting || isDisabled}
          className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting
            ? "Starter prosess…"
            : isDisabled
            ? "Sendt til Compliance"
            : "Send til Compliance"}
        </button>
      </div>
    </form>
  );
}

function RequestedProfessionalDraft({
  signees,
  selectedSignees,
  onSigneesChange,
  onSubmit,
  isSubmitting,
  isDisabled,
}: {
  signees: Signee[];
  selectedSignees: Signee[];
  onSigneesChange: (selected: Signee[]) => void;
  onSubmit: (variables: RequestedProfessionalVariables) => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
}) {
  const [scope, setScope] = useState("general");
  const [fileError, setFileError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFileError(null);
    if (selectedSignees.length === 0) {
      setFileError("Velg minst én signatar før du sender anmodningen.");
      return;
    }

    const formData = new FormData(event.currentTarget);
    let attachments: RequestedProfessionalVariables["attachments"];
    try {
      attachments = await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          mimeType: "application/pdf",
          content: await readFileAsDataUrl(file),
        }))
      );
    } catch (error) {
      console.error("Could not prepare customer classification data:", error);
      setFileError(
        error instanceof Error ? error.message : "Kunne ikke lese vedleggene."
      );
      return;
    }

    const value = (name: string) => formData.get(name);
    const yesNo = (name: string): boolean | null => {
      const answer = value(name);
      return answer === "yes" ? true : answer === "no" ? false : null;
    };
    await onSubmit({
      requestedScope: scope,
      requestedLimitation:
        scope === "limited"
          ? String(value("requestedLimitation") ?? "").trim()
          : "",
      transactionActivity: yesNo("transactionActivity"),
      financialPortfolioOver500000: yesNo("financialPortfolioOver500000"),
      relevantProfessionalExperience: yesNo("relevantProfessionalExperience"),
      additionalInformation: String(
        value("additionalInformation") ?? ""
      ).trim(),
      attachments,
      signees: selectedSignees,
    });
  };

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <h4 className="text-xs font-semibold">
        Anmodning om profesjonell kundestatus
      </h4>
      <label className={labelCls}>
        Ønsket omfang
        <select
          value={scope}
          onChange={(event) => setScope(event.target.value)}
          className={fieldCls}
        >
          <option value="general">Generelt</option>
          <option value="limited">Avgrenset</option>
        </select>
      </label>
      {scope === "limited" && (
        <label className={labelCls}>
          Eventuell avgrensning
          <input
            type="text"
            name="requestedLimitation"
            className={fieldCls}
          />
        </label>
      )}
      <div className={groupCls}>
        <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Grunnlag for vurderingen
        </h4>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Transaksjonsaktivitet",
            "Finansiell portefølje over EUR 500 000",
            "Relevant yrkeserfaring",
          ].map((criterion) => {
            const name =
              criterion === "Transaksjonsaktivitet"
                ? "transactionActivity"
                : criterion === "Finansiell portefølje over EUR 500 000"
                ? "financialPortfolioOver500000"
                : "relevantProfessionalExperience";
            return (
              <label key={criterion} className={labelCls}>
                {criterion}
                <select name={name} defaultValue="" className={fieldCls}>
                  <option value="">Velg</option>
                  <option value="yes">Ja</option>
                  <option value="no">Nei</option>
                </select>
              </label>
            );
          })}
        </div>
      </div>
      <label className={labelCls}>
        Utdypende opplysninger
        <textarea
          rows={3}
          name="additionalInformation"
          className={fieldCls}
        />
      </label>
      <div className={groupCls}>
        <p className="mb-3 text-xs font-medium">Hvem skal signere anmodningen?</p>
        <div className="h-[360px] overflow-hidden rounded-lg border border-border/60 [&>div]:h-full [&>div]:w-full [&>div]:border-0">
          <SigneeSelector
            signees={signees}
            selectedSignees={selectedSignees}
            onChange={onSigneesChange}
          />
        </div>
      </div>
      <ClassificationAttachments
        files={files}
        onChange={setFiles}
        disabled={isSubmitting || isDisabled}
      />
      {fileError && (
        <p role="alert" className="text-xs text-destructive">
          {fileError}
        </p>
      )}
      <button
        type="submit"
        disabled={
          isSubmitting || isDisabled || selectedSignees.length === 0
        }
        className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting
          ? "Sender til signering…"
          : isDisabled
          ? "Sendt til signering"
          : "Send til signering"}
      </button>
    </form>
  );
}

export function CustomerClassificationForm({
  onClose,
  contact,
  advisorContact,
  availableSignees,
}: {
  onClose: () => void;
  contact: Contact;
  advisorContact?: Contact;
  availableSignees: Signee[];
}) {
  const apolloClient = useApolloClient();
  const [selected, setSelected] = useState<ClassificationType | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedSignees, setSelectedSignees] = useState<Signee[]>([]);
  const [processState, setProcessState] = useState<ProcessState>({
    status: "idle",
  });
  const [statusRefreshError, setStatusRefreshError] = useState<string | null>(null);
  const submitInFlight = useRef(false);
  const reference = useRef<string | null>(null);
  const selectedOption = CLASSIFICATION_OPTIONS.find(
    (option) => option.value === selected
  );
  const isSubmitting = processState.status === "loading";

  const startProcess = async (submission: ClassificationSubmission) => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setStatusRefreshError(null);
    setProcessState({ status: "loading" });
    try {
      if (reference.current === null) reference.current = uuidv4();
      await apolloClient.mutate({
        mutation: CUSTOMER_CLASSIFICATION_START_PROCESS,
        variables: {
          key: CUSTOMER_CLASSIFICATION_PROCESS_KEY,
          data: {
            reference: reference.current,
            standardParams: getStdParams(contact, advisorContact),
            selectedContactId: contact.id,
            formData: submission,
          },
        },
      });
      setProcessState({
        status: "success",
      });
      try {
        await apolloClient.refetchQueries({
          include: ["GetDocProducerClassificationStatus"],
        });
      } catch (error) {
        console.error("Could not refresh customer classification status:", error);
        setStatusRefreshError(
          "Innsendingen er fullført, men statusen i headingen kunne ikke oppdateres. Åpne DocProducer på nytt for å hente statusen."
        );
      }
    } catch (error) {
      console.error("Could not start customer classification process:", error);
      setProcessState({
        status: "error",
        message:
          error instanceof Error
            ? error.message
            : "Kunne ikke starte prosessen. Prøv igjen.",
      });
    } finally {
      submitInFlight.current = false;
    }
  };

  const submitNaturalProfessional = (
    variables: NaturalProfessionalVariables
  ) =>
    startProcess({
      classificationType: "naturalProfessional",
      ...variables,
    });

  const submitRequestedProfessional = (
    variables: RequestedProfessionalVariables
  ) =>
    startProcess({
      classificationType: "professionalUponRequest",
      ...variables,
    });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-4 sm:p-5">
        <div className="mx-auto max-w-3xl overflow-hidden rounded-xl border border-border/80 bg-card p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/60 pb-4">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <UserCheck className="size-4" aria-hidden="true" />
              </span>
              <h2 className="text-sm font-semibold">Kundeklassifisering</h2>
            </div>
          </div>
          {(isSubmitting || processState.status === "success") && (
            <ComplianceSubmissionStatus
              isSending={isSubmitting}
              isSigning={selected === "requested"}
              contactName={contact.name}
              reference={reference.current}
              statusRefreshError={statusRefreshError}
            />
          )}
          {showDetails && selectedOption ? (
            <div
              hidden={isSubmitting || processState.status === "success"}
              className="mt-3 space-y-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">{selectedOption.label}</h3>
                <button
                  type="button"
                  disabled={processState.status === "success" || isSubmitting}
                  onClick={() => setShowDetails(false)}
                  className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Bytt variant
                </button>
              </div>
              {processState.status === "error" && (
                <div
                  role="alert"
                  className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive"
                >
                  <TriangleAlert
                    className="size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {processState.message}
                </div>
              )}
              {selected === "natural" ? (
                <NaturalProfessionalDraft
                  onSubmit={submitNaturalProfessional}
                  isSubmitting={isSubmitting}
                  isDisabled={processState.status === "success"}
                />
              ) : (
                <RequestedProfessionalDraft
                  signees={availableSignees}
                  selectedSignees={selectedSignees}
                  onSigneesChange={setSelectedSignees}
                  onSubmit={submitRequestedProfessional}
                  isSubmitting={isSubmitting}
                  isDisabled={processState.status === "success"}
                />
              )}
            </div>
          ) : (
            <div>
              <fieldset className="mt-2">
                <legend className="text-sm font-semibold">
                  Velg klassifiseringstype
                </legend>
                <p className="mt-1 mb-4 text-xs text-muted-foreground">
                  Velg løp og fortsett til utfylling.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {CLASSIFICATION_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        "group relative flex cursor-pointer select-none flex-col gap-5 overflow-hidden rounded-xl border p-5 text-xs transition-colors focus-within:ring-2 focus-within:ring-primary/30",
                        selected === option.value
                          ? "border-primary/70 bg-gradient-to-br from-primary/10 to-background shadow-sm"
                          : "border-border/80 bg-background hover:border-primary/40 hover:bg-muted/20"
                      )}
                    >
                      <input
                        type="radio"
                        name="customerClassification"
                        value={option.value}
                        checked={selected === option.value}
                        onChange={() => setSelected(option.value)}
                        className="sr-only"
                      />
                      <span className="flex w-full items-center justify-between">
                        <span className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <option.icon className="size-5" aria-hidden="true" />
                        </span>
                        <span
                          aria-hidden="true"
                          className={cn(
                            "flex size-5 items-center justify-center rounded-full border",
                            selected === option.value
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background"
                          )}
                        >
                          {selected === option.value && (
                            <CheckCircle2 className="size-4" />
                          )}
                        </span>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold leading-snug">
                          {option.label}
                        </span>
                        <span className="mt-1.5 block text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <Button
                type="button"
                size="sm"
                disabled={!selected}
                onClick={() => setShowDetails(true)}
                className="mt-5"
              >
                Fortsett til utfylling
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 justify-end border-t border-border bg-background px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          Lukk
        </button>
      </div>
    </div>
  );
}
